import type { Area } from 'react-easy-crop';

// Caps the longest edge so a phone photo doesn't become a 12MP avatar upload.
const DEFAULT_MAX_OUTPUT_PX = 2048;
const JPEG_QUALITY = 0.92;

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not load image for cropping'));
    image.src = url;
  });

/**
 * Renders `area` (pixels in the source image) onto a canvas and returns it as
 * a new File. PNG stays PNG so transparency survives; everything else is JPEG.
 */
export async function cropImageFile(
  file: File,
  area: Area,
  { maxOutputPx = DEFAULT_MAX_OUTPUT_PX }: { maxOutputPx?: number } = {},
): Promise<File> {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await loadImage(objectUrl);
    const scale = Math.min(1, maxOutputPx / Math.max(area.width, area.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(area.width * scale);
    canvas.height = Math.round(area.height * scale);

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas 2D context unavailable');
    }

    context.drawImage(
      image,
      area.x,
      area.y,
      area.width,
      area.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, type, JPEG_QUALITY),
    );
    if (!blob) {
      throw new Error('Canvas export failed');
    }

    return new File([blob], file.name, { type, lastModified: Date.now() });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
